<script setup lang="ts">
import { ref } from "vue";
import type { user } from "~/types/general";
import { FetchError } from "ofetch";

definePageMeta({
  middleware: ["$guest"],
});

interface ValidationError {
  [key: string]: string[];
}

const errors = ref<ValidationError>({});

const { login } = useSanctum();

const form = ref({
  email: "",
  password: "",
});
const router = useRouter();

const { isLoggedIn, user } = useSanctum<user>();

const submitForm = async () => {
  try {
    await login(form.value);
    router.push("/");
  } catch (e) {
    if (e instanceof FetchError && e.response?.status === 422) {
      // 422 validation error
      errors.value = e.response._data.errors;
    }
  }
};
</script>
<template>
  <div
    class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8"
  >
    <div class="sm:mx-auto sm:w-full sm:max-w-sm">
      <div class="flex justify-center">
        <NuxtLink to="/" class="">
          <div class="h-[50px] w-[250px] overflow-hidden">
            <img
              src="/images/Bali_Xperiences_Logo.png"
              alt="Logo"
              class="h-full w-full object-cover"
            />
          </div>
        </NuxtLink>
      </div>

      <h2 class="mt-10 text-center text-2xl/9 font-bold tracking-tight">
        Log in to your account
      </h2>
    </div>

    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
      <form class="space-y-6" @submit.prevent="submitForm">
        <div>
          <label
            for="email"
            class="block text-sm font-medium leading-6 text-gray-900"
            >Email address</label
          >
          <div class="mt-2">
            <input
              v-model="form.email"
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <span v-if="errors.email" class="text-red-500">{{
              errors.email[0]
            }}</span>
          </div>
        </div>

        <div>
          <div class="flex items-center justify-between">
            <label
              for="password"
              class="block text-sm font-medium leading-6 text-gray-900"
              >Password</label
            >
          </div>
          <div class="mt-2">
            <input
              v-model="form.password"
              id="password"
              name="password"
              type="password"
              autocomplete="current-password"
              class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <span v-if="errors.password" class="text-red-500">{{
              errors.password[0]
            }}</span>
          </div>
        </div>

        <div>
          <button
            type="submit"
            class="flex w-full justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Log in
          </button>
        </div>
      </form>
      <p class="mt-10 text-center text-sm/6 text-gray-400">
        Don't have an account?
        {{ " " }}
        <NuxtLink
          to="/register"
          class="font-semibold text-primary opacity-80 hover:text-indigo-300"
          >Register here
        </NuxtLink>
      </p>
    </div>
  </div>
</template>
